'use client';
// Gabarit de « dossier » — la fiche d'un objet qualité organisée en onglets,
// avec une barre d'action qui ne bouge pas.
//
// Les fiches (NC, réclamation, audit, action) empilaient toutes leurs sections
// dans une colonne unique : identité, contenu, actions liées, liens, journal.
// Sur un dossier vivant, le journal repoussait tout le reste hors écran, et les
// boutons de transition se déplaçaient verticalement selon la longueur du
// contenu — cause silencieuse de clic manqué.
//
// Écrit UNE fois ici plutôt que recopié dans les quatre écrans : c'est
// exactement la duplication qui a rendu `Ressources.tsx` ingérable.
import React from 'react';
import { I } from '@/components/ui';

export interface OngletDef<K extends string> {
  k: K;
  label: string;
  /** Compteur affiché dans l'onglet. `undefined` = pas de compteur. */
  n?: number;
  /** Glyphe devant le libellé. Servi d'abord par la ROUE des onglets
   *  « Paramétrage » : la roue signale un réglage partout où elle apparaît
   *  (décision du 24/08/2026), y compris au milieu d'une rangée d'onglets. */
  icone?: string;
}

/**
 * Barre d'onglets d'un dossier.
 *
 * Les onglets CACHENT : c'est leur intérêt et leur danger. Masquer les actions
 * correctives derrière un onglet ferait oublier qu'elles existent — d'où les
 * compteurs, qui ne sont pas décoratifs : ils disent ce qu'on ne voit pas.
 *
 * Clavier : flèches gauche/droite, et « roving tabindex » (seul l'onglet actif
 * est atteignable au Tab) — le motif ARIA attendu pour un tablist.
 */
export function OngletsDossier<K extends string>({ id, items, actif, onChange }: {
  /** préfixe des identifiants ARIA — unique par dossier affiché à l'écran */
  id: string;
  items: OngletDef<K>[];
  actif: K;
  onChange: (k: K) => void;
}) {
  const surTouche = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    onChange(items[(i + d + items.length) % items.length].k);
  };
  return (
    <div role="tablist" aria-label="Sections du dossier"
      style={{ display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid var(--border-default)' }}>
      {items.map((it, i) => {
        const on = actif === it.k;
        return (
          <button key={it.k} role="tab" id={`${id}-tab-${it.k}`} aria-selected={on} aria-controls={`${id}-panel-${it.k}`}
            tabIndex={on ? 0 : -1} onKeyDown={(e) => surTouche(e, i)} onClick={() => onChange(it.k)}
            className="ged-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: on ? 700 : 600,
              padding: '9px 13px', background: 'transparent',
              color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
              borderBottom: `2px solid ${on ? 'var(--color-primary)' : 'transparent'}`, marginBottom: -1,
            }}>
            {it.icone && <I n={it.icone} s={12} />}
            {it.label}
            {it.n !== undefined && it.n > 0 && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, background: on ? 'var(--cyan-50)' : 'var(--surface-sunken)', color: on ? 'var(--cyan-700)' : 'var(--text-muted)', borderRadius: 'var(--radius-pill)', padding: '1px 7px' }}>{it.n}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Panneau de l'onglet courant, relié à son onglet pour les lecteurs d'écran. */
export function PanneauDossier({ id, actif, children }: { id: string; actif: string; children: React.ReactNode }) {
  return (
    <div role="tabpanel" id={`${id}-panel-${actif}`} aria-labelledby={`${id}-tab-${actif}`}
      style={{ display: 'grid', gap: 14 }}>
      {children}
    </div>
  );
}

/**
 * Barre d'action persistante, en pied de dossier.
 *
 * `sticky` : elle reste visible quand la section consultée est longue (journal,
 * grille d'audit). Les marges négatives la font toucher les bords de la carte —
 * elle doit se lire comme un pied de page, pas comme un bloc flottant.
 */
/** Débord de la barre hors de la carte, des deux côtés et en bas.
 *
 *  ⚠️ La MÊME valeur sert deux fois, et c'est ce qui compte : en marge négative
 *  (pour le plein cadre) et en décalage `bottom` (pour le collage). `sticky`
 *  aligne la BOÎTE DE MARGE, pas la boîte de bordure — avec `bottom: 0` et une
 *  marge basse de −20, la barre remontait donc de 20 px et RECOUVRAIT les 8
 *  dernières lignes du contenu. Défaut vu par la RQ sur BIO24-REC-2026-007 :
 *  la dernière ligne de la réclamation était mangée par la barre.
 *
 *  Les deux valeurs doivent rester liées : les séparer, c'est reproduire le
 *  défaut à la première retouche de marge. */
const DEBORD = 20;

export function BarreAction({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      position: 'sticky', bottom: -DEBORD, zIndex: 2,
      display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
      borderTop: '1px solid var(--border-default)', background: '#fff',
      // 9 px et non 12 : la barre faisait 58 px de haut pour des boutons de 34,
      // soit près d'un quart de la hauteur utile d'une fiche courte. La RQ l'a
      // dite « trop haute de partout » — elle l'était.
      margin: `0 -${DEBORD}px -${DEBORD}px`, padding: '9px 20px',
    }}>
      {children}
    </div>
  );
}
